import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TipoCatalogo } from '@leadsphere/types';

import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useCanCatalogos } from '../../hooks/useCanCatalogos.js';
import { useToasts } from '../../hooks/useToasts.js';
import type { CatalogoItem } from '../clientes/types.js';
import { CatalogoCard } from './CatalogoCard.js';
import { TareasPorAreaCatalogo } from './TareasPorAreaCatalogo.js';
import type { CatalogoArea } from './types.js';

const CARDS: { tipo: TipoCatalogo; titulo: string }[] = [
  { tipo: 'giro', titulo: 'Giros' },
  { tipo: 'origen_lead', titulo: 'Origen de Lead' },
  { tipo: 'plataforma', titulo: 'Plataformas' },
  { tipo: 'tier', titulo: 'Tiers' },
  // Prompt BJ-10 — mismo patrón genérico, sin código especial: contrario a
  // 'area' (tarjeta aparte más abajo, endpointBase/permiso distintos).
  { tipo: 'puesto', titulo: 'Puestos' },
];

// Prompt AA — /catalogos ahora también lo alcanzan kam/comercial (para
// gestionar Áreas), que antes no entraban a esta página. canManageCatalogs
// sigue controlando Giro/Origen/Plataforma/Tiers/Tareas-por-Área; ese rol
// nuevo los ve en SOLO LECTURA (puedeGestionar=false en CatalogoCard), no se
// le oculta la página entera por un permiso que no tiene.

type CatalogosAgrupados = Record<TipoCatalogo, CatalogoItem[]>;

export function CatalogosPage() {
  const { get } = useApi();
  const { toasts, showToast, dismiss } = useToasts();
  const { canManageCatalogs, canManageAreaCatalogo } = useCanCatalogos();

  // GET /catalogos (no /catalogos/:tipo) — trae ítems activos e inactivos,
  // necesario para esta vista de administración (a diferencia de los
  // dropdowns del resto del sistema, que usan /catalogos/:tipo y solo ven activos).
  const { data, isLoading } = useQuery({
    queryKey: ['catalogos-admin'],
    queryFn: () => get<CatalogosAgrupados>('/catalogos'),
  });

  const { data: areasData, isLoading: areasLoading } = useQuery({
    queryKey: ['catalogos-area-admin'],
    queryFn: () => get<CatalogoArea[]>('/catalogos/area/todos'),
  });

  // Adapta {nombre} -> {tipo:'area', valor: nombre} para reutilizar
  // CatalogoCard tal cual (mismo contrato {id, valor, activo, orden} que
  // Giro/Origen/Plataforma/Tiers).
  const areasAdaptadas = useMemo<CatalogoItem[]>(
    () => (areasData ?? []).map(a => ({ id: a.id, tipo: 'area', valor: a.nombre, activo: a.activo, orden: a.orden })),
    [areasData],
  );

  return (
    <div>
      <div className="cat-topbar">
        <div className="cat-topbar-title">Catálogos</div>
      </div>

      <div className="cat-grid">
        {CARDS.map(({ tipo, titulo }) => (
          <CatalogoCard
            key={tipo}
            tipo={tipo}
            titulo={titulo}
            items={data?.[tipo] ?? []}
            isLoading={isLoading}
            onToast={showToast}
            puedeGestionar={canManageCatalogs}
          />
        ))}
        <CatalogoCard
          tipo="area"
          titulo="Áreas"
          items={areasAdaptadas}
          isLoading={areasLoading}
          onToast={showToast}
          endpointBase="/catalogos/area"
          puedeEliminar={false}
          invalidateKeys={[['catalogos-area-admin'], ['catalogo-area']]}
          puedeGestionar={canManageAreaCatalogo}
        />
        <TareasPorAreaCatalogo onToast={showToast} puedeGestionar={canManageCatalogs} />
      </div>

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
